import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { t, Trans } from "@lingui/macro";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";
import React, { useEffect, useRef, useState } from "react";
import { useLingui } from "@lingui/react";
import PhoneInputField from "@/main/account/(withSidebar)/edit-account/_components/phone-input-field";
import { useSession } from "next-auth/react";
import { Divider, useMediaQuery } from "@mui/material";
import { theme } from "@/styles/theme";
import { useGiftStore } from "@/components/context/gift-store";

interface Country {
  code: string;
  name: string;
  flag: string;
}
const DeliveryOptions = ({
  checkIfSubmitAndErroredFields,
}: {
  checkIfSubmitAndErroredFields: () => void;
}) => {
  const { i18n } = useLingui();
  const deliveryTimeRef = useRef<HTMLDivElement>(null);
  const deliveryMethodRef = useRef<HTMLDivElement>(null);
  const recipientDetailsRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const {
    errors,
    countryCode,
    deliveryDate,
    messageField,
    deliveryDateExact,
    deliveryMethod,
    name,
    email,
    phone,
    submitted,
    setDeliveryDate,
    setMessageField,
    setDeliveryDateExact,
    setDeliveryMethod,
    setName,
    setEmail,
    setPhone,
    scrollFromImage,
    setScrollFromImage,
    setCountryCode,
    finish,
  } = useGiftStore();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const countryList: Country[] = [
    { code: "41", name: "Switzerland (Schweiz)", flag: "/ch-flag.png" },
  ];
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const headerOffsetIfHeaderNotVisibile = 75;
  const headerOffsetIfHeaderVisibile = 125;

  const { data: session } = useSession();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const box = ref.current.getBoundingClientRect();
      const absoluteTop = box.top + window.scrollY;
      let offsetPosition = 0;

      if (window.scrollY > 100 || isMobile) {
        offsetPosition = absoluteTop - headerOffsetIfHeaderNotVisibile;
      } else {
        offsetPosition = absoluteTop - headerOffsetIfHeaderVisibile;
      }

      // Use requestAnimationFrame for more reliable timing
      requestAnimationFrame(() => {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      });
    }
  };

  useEffect(() => {
    if (scrollFromImage) {
      scrollToRef(deliveryTimeRef);
      setScrollFromImage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollFromImage]);

  const handleDeliveryDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryDate(event.target.value);
    if (event.target.value === "now") {
      requestAnimationFrame(() => {
        scrollToRef(deliveryMethodRef);
      });
    }
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageField(event.target.value);
  };

  const handleDeliveryMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (["link", "print"].includes(event.target.value)) {
      setDeliveryDate("now");
    }
    setDeliveryMethod(event.target.value);
    requestAnimationFrame(() => {
      scrollToRef(recipientDetailsRef);
    });
  };

  useEffect(() => {
    const currentTime = new Date();

    if (
      deliveryDate === "future" &&
      deliveryDateExact &&
      new Date(deliveryDateExact) > currentTime &&
      !isCalendarOpen
    ) {
      requestAnimationFrame(() => {
        scrollToRef(deliveryTimeRef);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate, deliveryDateExact, isCalendarOpen]);

  const handleDeliveryDateExactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryDateExact(event.target.value);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed, so add 1
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  useEffect(() => {
    if (finish) {
      checkIfSubmitAndErroredFields();

      if (!submitted && !(deliveryDate === "future" && isCalendarOpen)) {
        // Map fields and their refs
        const fieldRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {
          deliveryDate: deliveryTimeRef,
          deliveryMethod: deliveryMethodRef,
          name: recipientDetailsRef,
          email: recipientDetailsRef,
          phone: recipientDetailsRef,
          messageField: messageRef,
        };

        const deliverInFutureFocused =
          document.activeElement && document.activeElement.getAttribute("name") === "future";

        const invalidField = [
          "deliveryDate",
          "deliveryMethod",
          "name",
          "email",
          "phone",
          "messageField",
        ].find((field) => {
          if (field === "email" && deliveryMethod !== "email") return false;
          if (field === "phone" && deliveryMethod !== "sms") return false;
          const emailFocused =
            document.activeElement && document.activeElement.getAttribute("name") === "email";
          const phoneFocused =
            document.activeElement && document.activeElement.getAttribute("name") === "phone";
          switch (field) {
            case "deliveryDate":
              return !deliveryDate;
            case "deliveryMethod":
              return !deliveryMethod;
            case "name":
              return !name && !deliverInFutureFocused;
            case "email":
              return (
                (!email || errors.find((element) => element.key == "email")?.error) &&
                !deliverInFutureFocused
              );
            case "phone":
              return (
                (!phone || errors.find((element) => element.key == "phone")?.error) &&
                !deliverInFutureFocused
              );
            case "messageField":
              return !messageField && !emailFocused && !phoneFocused && !deliverInFutureFocused;
            default:
              return false;
          }
        });

        if (invalidField && fieldRefs[invalidField]) {
          requestAnimationFrame(() => {
            scrollToRef(fieldRefs[invalidField]);
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finish, deliveryDate, deliveryMethod, name, email, phone, submitted, isCalendarOpen]);

  return (
    <>
      <Box ref={deliveryTimeRef}>
        <Typography
          sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
        >
          <Trans>When do you want to deliver it</Trans>?
        </Typography>
        <Box>
          <FormControl sx={{ width: "100%" }}>
            <RadioGroup
              aria-labelledby="delivery-date-radio-buttons-group"
              name="delivery-date-radio-buttons-group"
              value={deliveryDate}
              onChange={handleDeliveryDateChange}
            >
              <FormControlLabel
                sx={{
                  borderRadius: "6px",
                  border: "solid 2px #bcbfc1",
                  padding: "0 14px 0 0",
                  height: "50px",
                  margin: "0 0 12px",
                  width: "100%",
                  color: finish && !deliveryDate ? "#a94442" : "#000",
                  ".MuiSvgIcon-root": {
                    fontSize: "28px",
                    fontWeight: "500",
                  },
                  ".MuiTypography-root": {
                    fontSize: "17px",
                    fontWeight: "500",
                  },
                }}
                value="now"
                control={<Radio disableRipple />}
                label={t(i18n)`Deliver as soon as possible`}
              />
              {!["link", "print"].includes(deliveryMethod) && (
                <FormControlLabel
                  name="future"
                  sx={{
                    borderRadius: "6px",
                    border: "solid 2px #bcbfc1",
                    padding: "0 14px 0 0",
                    height: "50px",
                    margin: "0 0 12px",
                    width: "100%",
                    color: finish && !deliveryDate ? "#a94442" : "#000",
                    ".MuiSvgIcon-root": {
                      fontSize: "28px",
                      fontWeight: "500",
                    },
                    ".MuiTypography-root": {
                      fontSize: "17px",
                      fontWeight: "500",
                    },
                  }}
                  value="future"
                  control={<Radio disableRipple />}
                  label={t(i18n)`Pick a date`}
                />
              )}
            </RadioGroup>
          </FormControl>
          {deliveryDate === "future" && (
            <div ref={calendarRef}>
              <TextField
                type="datetime-local"
                value={deliveryDateExact}
                onChange={handleDeliveryDateExactChange}
                onFocus={() => setIsCalendarOpen(true)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: getCurrentDateTime(),
                }}
              />
            </div>
          )}
        </Box>
      </Box>
      <Divider sx={{ margin: "40px 0", borderColor: "#d2d2d7", width: "100%" }} />
      <Box ref={deliveryMethodRef}>
        <Typography
          sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
        >
          <Trans>How do you want to deliver your voucher</Trans>?
        </Typography>
        <FormControl sx={{ width: "100%" }}>
          <RadioGroup
            aria-labelledby="delivery-method-radio-buttons-group"
            name="delivery-method-radio-buttons-group"
            value={deliveryMethod}
            onChange={handleDeliveryMethodChange}
          >
            <Box sx={{ position: "relative" }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#000",
                  fontWeight: 400,
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  opacity: 0.5,
                }}
              >
                (<Trans>sent by offerz</Trans>)
              </Typography>
              <FormControlLabel
                sx={{
                  borderRadius: "6px",
                  border: "solid 2px #bcbfc1",
                  padding: "0 14px 0 0",
                  height: "50px",
                  margin: "0 0 12px",
                  width: "100%",
                  color: finish && !deliveryMethod ? "#a94442" : "#000",
                  ".MuiSvgIcon-root": {
                    fontSize: "28px",
                    fontWeight: "500",
                  },
                  ".MuiTypography-root": {
                    fontSize: "17px",
                    fontWeight: "500",
                  },
                }}
                value="email"
                control={<Radio disableRipple />}
                label={t(i18n)`E-Mail`}
              />
            </Box>
            <Box sx={{ position: "relative" }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#000",
                  fontWeight: 400,
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  opacity: 0.5,
                }}
              >
                (<Trans>sent by offerz</Trans>)
              </Typography>
              <FormControlLabel
                sx={{
                  borderRadius: "6px",
                  border: "solid 2px #bcbfc1",
                  padding: "0 14px 0 0",
                  height: "50px",
                  margin: "0 0 12px",
                  width: "100%",
                  color: finish && !deliveryMethod ? "#a94442" : "#000",
                  ".MuiSvgIcon-root": {
                    fontSize: "28px",
                    fontWeight: "500",
                  },
                  ".MuiTypography-root": {
                    fontSize: "17px",
                    fontWeight: "500",
                  },
                }}
                value="sms"
                control={<Radio disableRipple />}
                label={t(i18n)`SMS`}
              />
            </Box>
            <FormControlLabel
              sx={{
                borderRadius: "6px",
                border: "solid 2px #bcbfc1",
                padding: "0 14px 0 0",
                height: "50px",
                margin: "0 0 12px",
                width: "100%",
                color: finish && !deliveryMethod ? "#a94442" : "#000",
                ".MuiSvgIcon-root": {
                  fontSize: "28px",
                  fontWeight: "500",
                },
                ".MuiTypography-root": {
                  fontSize: "17px",
                  fontWeight: "500",
                },
              }}
              value="link"
              control={<Radio disableRipple />}
              label={t(i18n)`Share a link`}
            />
            <FormControlLabel
              sx={{
                borderRadius: "6px",
                border: "solid 2px #bcbfc1",
                padding: "0 14px 0 0",
                height: "50px",
                margin: "0 0 12px",
                width: "100%",
                color: finish && !deliveryMethod ? "#a94442" : "#000",
                ".MuiSvgIcon-root": {
                  fontSize: "28px",
                  fontWeight: "500",
                },
                ".MuiTypography-root": {
                  fontSize: "17px",
                  fontWeight: "500",
                },
              }}
              value="print"
              control={<Radio disableRipple />}
              label={t(i18n)`Print`}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider sx={{ margin: "40px 0", borderColor: "#d2d2d7", width: "100%" }} />{" "}
      <Box ref={recipientDetailsRef}>
        <Typography
          sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
        >
          <Trans>Recipient&apos;s details</Trans>?
        </Typography>
        <TextField
          sx={{
            margin: "5px 0px",
            "& .MuiOutlinedInput-root": {
              fontSize: "17px",
              lineHeight: "1.35",
              color: "#1f1f25",
              padding: "12px 10px 12px",
              width: "100%",
              backgroundColor: finish && !name ? "#f2dede" : "transparent",
              border: "2px solid #bcbfc1",
              ".MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
            },
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
          }}
          placeholder={t(i18n)`Enter name...`}
          variant="outlined"
          value={name}
        />
        {deliveryMethod === "email" && (
          <>
            <TextField
              sx={{
                margin: "5px 0px",
                "& .MuiOutlinedInput-root": {
                  fontSize: "17px",
                  lineHeight: "1.35",
                  color: "#1f1f25",
                  padding: "12px 10px 12px",
                  width: "100%",
                  backgroundColor: finish && !email ? "#f2dede" : "transparent",
                  border: "2px solid #bcbfc1",
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "none !important",
                  },
                },
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              name="email"
              placeholder={name ? t(i18n)`${name}'s email` : t(i18n)`E-Mail`}
              variant="outlined"
              value={email}
              onBlur={() => {
                if (email) scrollToRef(messageRef);
              }}
            />
            {finish && errors && errors.find((element) => element.key == "email") && (
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#fd7b7c" }}>
                {errors.find((element) => element.key == "email")?.error}
              </Typography>
            )}
            {session && email === session.user.email && (
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#fd7b7c" }}>
                <Trans>Are you sure, you want to send the gift to yourself?</Trans>
              </Typography>
            )}
          </>
        )}
        {deliveryMethod === "sms" && (
          <>
            <PhoneInputField
              countryList={countryList}
              value={phone}
              name="phone"
              label={""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPhone(event.target.value);
              }}
              style={{
                borderRadius: "8px",
                backgroundColor: finish && !phone ? "#f2dede" : "transparent",
                border: "2px solid #bcbfc1",
                marginBottom: "5px",
              }}
              setCountryCode={setCountryCode}
              countryCode={countryCode}
              selectedCountry={countryList[0]}
              placeholder={name ? t(i18n)`${name}'s phone number` : t(i18n)`Phone number`}
              onBlur={() => {
                if (phone) scrollToRef(messageRef);
              }}
            />

            {finish && errors && errors.find((element) => element.key == "phone") && (
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#fd7b7c" }}>
                {errors.find((element) => element.key == "phone")?.error}
              </Typography>
            )}
          </>
        )}
      </Box>
      <Divider sx={{ margin: "40px 0", borderColor: "#d2d2d7", width: "100%" }} />
      <Box ref={messageRef}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography
            sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
          >
            <Trans>Message for</Trans>&nbsp;{name}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: messageField.length >= 500 ? "red" : "#8b8b92",
              marginBottom: "20px",
            }}
          >
            <Trans>{messageField.length}/500</Trans>
          </Typography>
        </Box>
        <textarea
          style={{
            backgroundColor: finish && !messageField ? "#f2dede" : "#f6f6f6",
            width: "100%",
            borderRadius: "6px",
            minHeight: "130px",
            padding: "15px",
            fontSize: "17px",
            outline: "none",
          }}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              handleMessageChange(e);
            }
          }}
          value={messageField}
          maxLength={500}
        />
      </Box>
    </>
  );
};

export default DeliveryOptions;
